import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import "regenerator-runtime/runtime.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/es.string.trim.js";
import "core-js/modules/web.dom-collections.iterator.js";
import "core-js/modules/web.timers.js";
import { defineComponent as _defineComponent } from 'vue';
import { useHead } from '@unhead/vue';
import { IMPACT_CARRY_CONCEPT_ID, IMPACT_STRATEGY_CONCEPT_ID } from '~/constants/concepts';
import { usePortfolioReviewContext } from '~/functions/review';
import { shouldRenderMetricAssignment } from '~/functions/impact-carry-kpi';
import { buildReviewCompanyExportFileName, deliverReviewCompanyExportEmbedded, deliverReviewCompanyExportStandalone, failReviewCompanyExport } from '~/functions/review-export';
import { useGetImpactGoalQuery, useGetReviewRequestQuery } from '~/types/types';

/** Safety cap for slow machines; typical prep is well under 5s. */

export default /*#__PURE__*/_defineComponent({
  __name: '_ventureId',
  setup: function setup(__props) {
    var PREPARE_EXPORT_MAX_MS = 15000;
    var EXPORT_READY_POLL_MS = 100;
    var EXPORT_READY_MAX_ATTEMPTS = 120;
    var EXPORT_POST_READY_SETTLE_MS = 500;
    var route = useRoute();
    var ventureId = computed(function () {
      return route.params.ventureId;
    });
    var requestId = computed(function () {
      return route.params.requestId;
    });
    var isEmbedded = computed(function () {
      return route.query.embedded === '1';
    });
    var shouldAutoPrint = computed(function () {
      return route.query.autoprint === '1';
    });
    var shouldDeliverExport = computed(function () {
      return isEmbedded.value || shouldAutoPrint.value;
    });
    var hasStartedExportDelivery = ref(false);
    var exportDocumentRef = ref(null);
    var exportedAt = ref(new Date());
    var _usePortfolioReviewCo = usePortfolioReviewContext(),
      hasReviewerRole = _usePortfolioReviewCo.hasReviewerRole,
      accountLoading = _usePortfolioReviewCo.loading;
    var reviewExportQueriesEnabled = computed(function () {
      return !accountLoading.value && hasReviewerRole.value && !!requestId.value;
    });
    var _useGetReviewRequestQ = useGetReviewRequestQuery(computed(function () {
        return {
          id: requestId.value
        };
      }), computed(function () {
        return {
          enabled: reviewExportQueriesEnabled.value
        };
      })),
      requestResult = _useGetReviewRequestQ.result,
      requestLoading = _useGetReviewRequestQ.loading;
    var ventureRow = computed(function () {
      var _requestResult$value, _requestResult$value$, _requestResult$value$2;
      return (_requestResult$value = requestResult.value) === null || _requestResult$value === void 0 ? void 0 : (_requestResult$value$ = _requestResult$value.getReviewRequest) === null || _requestResult$value$ === void 0 ? void 0 : (_requestResult$value$2 = _requestResult$value$.ventures) === null || _requestResult$value$2 === void 0 ? void 0 : _requestResult$value$2.find(function (venture) {
        return venture.ventureId === ventureId.value;
      });
    });
    var companyName = computed(function () {
      var _ventureRow$value, _ventureRow$value$ven, _ventureRow$value$ven2;
      return ((_ventureRow$value = ventureRow.value) === null || _ventureRow$value === void 0 ? void 0 : (_ventureRow$value$ven = _ventureRow$value.venture) === null || _ventureRow$value$ven === void 0 ? void 0 : (_ventureRow$value$ven2 = _ventureRow$value$ven.name) === null || _ventureRow$value$ven2 === void 0 ? void 0 : _ventureRow$value$ven2.trim()) || '—';
    });
    var exportFileBaseName = computed(function () {
      return buildReviewCompanyExportFileName({
        companyName: companyName.value,
        ventureId: ventureId.value,
        exportedAt: exportedAt.value
      });
    });
    useHead({
      title: exportFileBaseName
    });
    var _useGetImpactGoalQuer = useGetImpactGoalQuery(computed(function () {
        return {
          ventureId: ventureId.value
        };
      }), computed(function () {
        return {
          enabled: reviewExportQueriesEnabled.value && !!ventureId.value && !!ventureRow.value,
          fetchPolicy: 'cache-and-network'
        };
      })),
      impactGoalResult = _useGetImpactGoalQuer.result,
      impactGoalQueryLoading = _useGetImpactGoalQuer.loading;
    var impactGoal = computed(function () {
      var _impactGoalResult$val;
      return (_impactGoalResult$val = impactGoalResult.value) === null || _impactGoalResult$val === void 0 ? void 0 : _impactGoalResult$val.getImpactGoal;
    });
    var tocImpact = computed(function () {
      var _impactGoal$value, _impactGoal$value$imp;
      return (_impactGoal$value = impactGoal.value) === null || _impactGoal$value === void 0 ? void 0 : (_impactGoal$value$imp = _impactGoal$value.impacts) === null || _impactGoal$value$imp === void 0 ? void 0 : _impactGoal$value$imp.find(function (impact) {
        return Number(impact.conceptId) === IMPACT_STRATEGY_CONCEPT_ID;
      });
    });
    var primaryCarryImpact = computed(function () {
      var _impactGoal$value$imp2, _impactGoal$value2;
      return ((_impactGoal$value$imp2 = (_impactGoal$value2 = impactGoal.value) === null || _impactGoal$value2 === void 0 ? void 0 : _impactGoal$value2.impacts) !== null && _impactGoal$value$imp2 !== void 0 ? _impactGoal$value$imp2 : []).find(function (impact) {
        return Number(impact.conceptId) === IMPACT_CARRY_CONCEPT_ID;
      });
    });
    var expectedCarryKpiCount = computed(function () {
      var _primaryCarryImpact$v;
      if (!primaryCarryImpact.value) {
        return 0;
      }
      return ((_primaryCarryImpact$v = primaryCarryImpact.value.metricAssignments) !== null && _primaryCarryImpact$v !== void 0 ? _primaryCarryImpact$v : []).filter(shouldRenderMetricAssignment).length;
    });
    var exportPhase = computed(function () {
      var _requestResult$value2;
      if (accountLoading.value) {
        return 'loading';
      }
      if (!hasReviewerRole.value) {
        return 'no-access';
      }
      if (requestLoading.value || !((_requestResult$value2 = requestResult.value) !== null && _requestResult$value2 !== void 0 && _requestResult$value2.getReviewRequest)) {
        return 'loading';
      }
      if (!ventureRow.value) {
        return 'no-venture';
      }
      if (impactGoalQueryLoading.value && !impactGoal.value) {
        return 'loading';
      }
      return 'ready';
    });
    var isLoading = computed(function () {
      return exportPhase.value === 'loading';
    });
    var isExportContentReady = function isExportContentReady() {
      var host = document.querySelector('.card-export-root');
      if (!host) {
        return false;
      }
      var strategyPage = host.querySelector('.card-export-page');
      var carryFlow = host.querySelector('.export-carry-flow');
      if (!strategyPage || !carryFlow) {
        return false;
      }
      var strategyFrame = strategyPage.querySelector('.card-export-page__body');
      if (!strategyFrame || strategyFrame.clientWidth < 400) {
        return false;
      }
      if (expectedCarryKpiCount.value === 0) {
        return true;
      }
      var renderedKpiCount = carryFlow.querySelectorAll('.impact-carry-kpi-section').length;
      return renderedKpiCount >= expectedCarryKpiCount.value;
    };
    var waitForExportReady = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator(/*#__PURE__*/regeneratorRuntime.mark(function _callee() {
        var attempt;
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              attempt = 0;
            case 1:
              if (!(attempt < EXPORT_READY_MAX_ATTEMPTS)) {
                _context.next = 11;
                break;
              }
              _context.next = 4;
              return nextTick();
            case 4:
              if (!isExportContentReady()) {
                _context.next = 6;
                break;
              }
              return _context.abrupt("return");
            case 6:
              _context.next = 8;
              return new Promise(function (resolve) {
                return window.setTimeout(resolve, EXPORT_READY_POLL_MS);
              });
            case 8:
              attempt += 1;
              _context.next = 1;
              break;
            case 11:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function waitForExportReady() {
        return _ref.apply(this, arguments);
      };
    }();
    var runRecalculatePasses = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator(/*#__PURE__*/regeneratorRuntime.mark(function _callee2() {
        var _exportDocumentRef$va, _exportDocumentRef$va2, _exportDocumentRef$va3;
        return regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return new Promise(function (resolve) {
                return requestAnimationFrame(function () {
                  return requestAnimationFrame(resolve);
                });
              });
            case 2:
              (_exportDocumentRef$va = exportDocumentRef.value) === null || _exportDocumentRef$va === void 0 ? void 0 : _exportDocumentRef$va.recalculateAll();
              _context2.next = 5;
              return new Promise(function (resolve) {
                return window.setTimeout(resolve, 150);
              });
            case 5:
              (_exportDocumentRef$va2 = exportDocumentRef.value) === null || _exportDocumentRef$va2 === void 0 ? void 0 : _exportDocumentRef$va2.recalculateAll();
              _context2.next = 8;
              return new Promise(function (resolve) {
                return window.setTimeout(resolve, 150);
              });
            case 8:
              (_exportDocumentRef$va3 = exportDocumentRef.value) === null || _exportDocumentRef$va3 === void 0 ? void 0 : _exportDocumentRef$va3.recalculateAll();
            case 9:
            case "end":
              return _context2.stop();
          }
        }, _callee2);
      }));
      return function runRecalculatePasses() {
        return _ref2.apply(this, arguments);
      };
    }();
    var prepareCardsForExport = /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator(/*#__PURE__*/regeneratorRuntime.mark(function _callee3() {
        var _exportDocumentRef$va4;
        return regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              _context3.next = 2;
              return waitForExportReady();
            case 2:
              _context3.next = 4;
              return new Promise(function (resolve) {
                return window.setTimeout(resolve, EXPORT_POST_READY_SETTLE_MS);
              });
            case 4:
              _context3.next = 6;
              return runRecalculatePasses();
            case 6:
              _context3.next = 8;
              return new Promise(function (resolve) {
                return window.setTimeout(resolve, EXPORT_POST_READY_SETTLE_MS);
              });
            case 8:
              (_exportDocumentRef$va4 = exportDocumentRef.value) === null || _exportDocumentRef$va4 === void 0 ? void 0 : _exportDocumentRef$va4.recalculateAll();
            case 9:
            case "end":
              return _context3.stop();
          }
        }, _callee3);
      }));
      return function prepareCardsForExport() {
        return _ref3.apply(this, arguments);
      };
    }();
    var deliverExport = /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator(/*#__PURE__*/regeneratorRuntime.mark(function _callee4() {
        var documentTitle;
        return regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) switch (_context4.prev = _context4.next) {
            case 0:
              documentTitle = exportFileBaseName.value;
              if (!isEmbedded.value) {
                _context4.next = 5;
                break;
              }
              _context4.next = 4;
              return deliverReviewCompanyExportEmbedded(ventureId.value, {
                documentTitle: documentTitle
              });
            case 4:
              return _context4.abrupt("return");
            case 5:
              if (!shouldAutoPrint.value) {
                _context4.next = 8;
                break;
              }
              _context4.next = 8;
              return deliverReviewCompanyExportStandalone(ventureId.value, {
                documentTitle: documentTitle
              });
            case 8:
            case "end":
              return _context4.stop();
          }
        }, _callee4);
      }));
      return function deliverExport() {
        return _ref4.apply(this, arguments);
      };
    }();
    var runExportDelivery = /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator(/*#__PURE__*/regeneratorRuntime.mark(function _callee5() {
        return regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) switch (_context5.prev = _context5.next) {
            case 0:
              if (!(hasStartedExportDelivery.value || !shouldDeliverExport.value)) {
                _context5.next = 2;
                break;
              }
              return _context5.abrupt("return");
            case 2:
              hasStartedExportDelivery.value = true;
              _context5.prev = 3;
              _context5.next = 6;
              return Promise.race([prepareCardsForExport(), new Promise(function (resolve) {
                return window.setTimeout(resolve, PREPARE_EXPORT_MAX_MS);
              })]);
            case 6:
              _context5.prev = 6;
              _context5.next = 9;
              return deliverExport();
            case 9:
              return _context5.finish(6);
            case 10:
            case "end":
              return _context5.stop();
          }
        }, _callee5, null, [[3,, 6, 10]]);
      }));
      return function runExportDelivery() {
        return _ref5.apply(this, arguments);
      };
    }();
    watch(exportPhase, /*#__PURE__*/function () {
      var _ref6 = _asyncToGenerator(/*#__PURE__*/regeneratorRuntime.mark(function _callee6(phase) {
        return regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) switch (_context6.prev = _context6.next) {
            case 0:
              if (!(phase === 'loading')) {
                _context6.next = 2;
                break;
              }
              return _context6.abrupt("return");
            case 2:
              if (!(phase === 'no-access' || phase === 'no-venture')) {
                _context6.next = 5;
                break;
              }
              if (shouldDeliverExport.value) {
                failReviewCompanyExport(ventureId.value);
              }
              return _context6.abrupt("return");
            case 5:
              if (!(phase === 'ready')) {
                _context6.next = 9;
                break;
              }
              exportedAt.value = new Date();
              _context6.next = 9;
              return runExportDelivery();
            case 9:
            case "end":
              return _context6.stop();
          }
        }, _callee6);
      }));
      return function (_x) {
        return _ref6.apply(this, arguments);
      };
    }(), {
      immediate: true
    });
    return {
      __sfc: true,
      PREPARE_EXPORT_MAX_MS: PREPARE_EXPORT_MAX_MS,
      EXPORT_READY_POLL_MS: EXPORT_READY_POLL_MS,
      EXPORT_READY_MAX_ATTEMPTS: EXPORT_READY_MAX_ATTEMPTS,
      EXPORT_POST_READY_SETTLE_MS: EXPORT_POST_READY_SETTLE_MS,
      route: route,
      ventureId: ventureId,
      requestId: requestId,
      isEmbedded: isEmbedded,
      shouldAutoPrint: shouldAutoPrint,
      shouldDeliverExport: shouldDeliverExport,
      hasStartedExportDelivery: hasStartedExportDelivery,
      exportDocumentRef: exportDocumentRef,
      exportedAt: exportedAt,
      hasReviewerRole: hasReviewerRole,
      accountLoading: accountLoading,
      reviewExportQueriesEnabled: reviewExportQueriesEnabled,
      requestResult: requestResult,
      requestLoading: requestLoading,
      ventureRow: ventureRow,
      companyName: companyName,
      exportFileBaseName: exportFileBaseName,
      impactGoalResult: impactGoalResult,
      impactGoalQueryLoading: impactGoalQueryLoading,
      impactGoal: impactGoal,
      tocImpact: tocImpact,
      primaryCarryImpact: primaryCarryImpact,
      expectedCarryKpiCount: expectedCarryKpiCount,
      exportPhase: exportPhase,
      isLoading: isLoading,
      isExportContentReady: isExportContentReady,
      waitForExportReady: waitForExportReady,
      runRecalculatePasses: runRecalculatePasses,
      prepareCardsForExport: prepareCardsForExport,
      deliverExport: deliverExport,
      runExportDelivery: runExportDelivery
    };
  }
});